import React, { useState, useLayoutEffect } from "react";
import { IconContext } from "react-icons";
import {
    FaAngleDown,
    FaAngleUp,
    FaJsSquare,
    FaPython,
    FaGem,
    FaSwift,
    FaJava,
    FaGoogle,
    FaAndroid,
    FaPhp,
    FaRProject,
    FaBars,
} from "react-icons/fa";
import { SiCplusplus } from "react-icons/si";
import { BsCursorText } from "react-icons/bs";
import "font-awesome/css/font-awesome.min.css";
import "./Dropdown.css";
import { DropdownProps } from "../types";

const ICONS: { [key: string]: React.ComponentType } = {
    FaJsSquare: FaJsSquare,
    FaPython: FaPython,
    FaGem: FaGem,
    FaSwift: FaSwift,
    FaJava: FaJava,
    FaGoogle: FaGoogle,
    FaAndroid: FaAndroid,
    FaPhp: FaPhp,
    FaRProject: FaRProject,
    FaBars: FaBars,
    SiCplusplus: SiCplusplus,
    BsCursorText: BsCursorText,
};

const LANG_ICON_MAP: { [key: string]: React.ComponentType } = {
    Python: FaPython,
    Javascript: FaJsSquare,
    Ruby: FaGem,
    Swift: FaSwift,
    Java: FaJava,
    Go: FaGoogle,
    Kotlin: FaAndroid,
    PHP: FaPhp,
    R: FaRProject,
    Scala: FaBars,
    "C++": SiCplusplus,
    plaintext: BsCursorText,
};

const Dropdown: React.FC<DropdownProps> = ({ title, list, toggleItem, currLang }) => {
    const [listOpen, setListOpen] = useState<boolean>(false);
    const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
    const dropdownRef = React.useRef<HTMLUListElement>(null);
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    const toggleList = (): void => setListOpen((prevState) => !prevState);

    // Position dropdown with fixed coordinates so it's never clipped by overflow:hidden ancestors.
    // Also handle top/bottom viewport overflow.
    useLayoutEffect(() => {
        if (!listOpen || !dropdownRef.current || !wrapperRef.current) {
            setDropdownPosition(null);
            return;
        }
        const wrapperRect = wrapperRef.current.getBoundingClientRect();
        const dropdownRect = dropdownRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const dropdownHeight = dropdownRect.height || 200;
        const dropdownWidth = dropdownRect.width || 120;
        const margin = 8;

        // Default: to the left of wrapper, vertically centered
        let left = wrapperRect.left - dropdownWidth - margin;
        let top = wrapperRect.top + (wrapperRect.height / 2) - (dropdownHeight / 2);

        // On small screens the dropdown may open to the right (see media query)
        const isRightSide = window.innerWidth <= 390;
        if (isRightSide) {
            left = wrapperRect.right + margin;
        }

        // Clamp left to viewport
        left = Math.max(margin, Math.min(left, viewportWidth - dropdownWidth - margin));

        // Fix top overflow: if top would be cut off, align dropdown top with wrapper so it extends downward
        if (top < margin) {
            top = Math.max(margin, wrapperRect.top);
        }
        // Fix bottom overflow: if bottom would be cut off, align dropdown bottom with wrapper so it extends upward
        if (top + dropdownHeight > viewportHeight - margin) {
            top = Math.min(viewportHeight - dropdownHeight - margin, wrapperRect.bottom - dropdownHeight);
        }

        setDropdownPosition({ top, left });
    }, [listOpen]);

    return (
        <div className="dd-wrapper" ref={wrapperRef} onMouseEnter={toggleList} onMouseLeave={toggleList}>
            <div className="dd-header">
                <div className="dd-header-title">
                    {title}
                    {listOpen ? <FaAngleUp /> : <FaAngleDown />}
                </div>
            </div>
            {listOpen && (
                <ul
                    className="dd-list dd-list-fixed"
                    ref={dropdownRef}
                    style={
                        dropdownPosition
                            ? { top: dropdownPosition.top, left: dropdownPosition.left }
                            : { visibility: "hidden" as const }
                    }
                >
                    {list.map((item) => (
                        <li
                            className="dd-list-item"
                            key={item.id}
                            onClick={() => toggleItem(item.id, item.key)}
                        >
                            {ICONS[item.fa_icon] && React.createElement(ICONS[item.fa_icon])}
                            {item.title}
                        </li>
                    ))}
                </ul>
            )}
            <span className="btn__label">
                <IconContext.Provider value={{ size: "0.5rem" }}>
                    {LANG_ICON_MAP[currLang] && React.createElement(LANG_ICON_MAP[currLang])}
                    {" "}
                    {currLang}
                </IconContext.Provider>
            </span>
        </div>
    );
};

export default Dropdown;
